import { supabase } from './supabaseClient';
import {
  assertPropertyAccess,
  PropertyAuthorizationError,
} from './propertyAuthorization';
import {
  validateRequiredFields,
  logError,
  ERROR_CODES,
  type DatabaseError,
} from '../utils/errorHandling';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_REGEX.test(value);
}

export type LimitScope = 'property' | 'room_type';

export interface RoomTypeLimit {
  id: string;
  propertyId: string;
  roomTypeId: string | null;
  minPrice: number;
  maxPrice: number;
  currency: string;
  validFrom: string | null;
  validUntil: string | null;
  scope: LimitScope;
  label: string | null;
  lastUpdated?: Date;
}

export interface PriceLimitTimeframe {
  id?: string;
  label: string | null;
  validFrom: string | null;
  validUntil: string | null;
  scope: LimitScope;
  roomTypeId: string | null;
  minPrice: number;
  maxPrice: number;
  currency: string;
}

export interface PriceLimitViolation {
  type: 'min' | 'max';
  currentPrice: number;
  limitPrice: number;
  roomTypeId: string;
  propertyId: string;
  message: string;
}

function mapRowToLimit(item: Record<string, unknown>): RoomTypeLimit {
  return {
    id: item.id as string,
    propertyId: item.property_id as string,
    roomTypeId: (item.room_type_id as string) || null,
    minPrice: Number(item.min_price),
    maxPrice: Number(item.max_price),
    currency: item.currency as string,
    validFrom: (item.valid_from as string) || null,
    validUntil: (item.valid_until as string) || null,
    scope: (item.scope as LimitScope) || 'room_type',
    label: (item.label as string) || null,
    lastUpdated: item.updated_at
      ? new Date(item.updated_at as string)
      : undefined,
  };
}

export class RoomTypeLimitsService {
  private propertyCache = new Map<string, RoomTypeLimit[]>();
  private propertyCacheExpiry = new Map<string, number>();
  private readonly CACHE_DURATION = 10 * 60 * 1000;

  async getAllPropertyLimits(propertyId: string): Promise<RoomTypeLimit[]> {
    const cacheKey = `property-${propertyId}`;

    if (this.isPropertyCacheValid(cacheKey)) {
      return this.propertyCache.get(cacheKey) || [];
    }

    try {
      const { data, error } = await supabase
        .from('room_type_limits')
        .select('*')
        .eq('property_id', propertyId);

      if (error) throw error;

      const limits = (data || []).map(mapRowToLimit);

      this.propertyCache.set(cacheKey, limits);
      this.propertyCacheExpiry.set(cacheKey, Date.now() + this.CACHE_DURATION);

      return limits;
    } catch (error) {
      console.error('Error fetching all property limits:', error);
      return [];
    }
  }

  getEffectiveLimitForDate(
    allLimits: RoomTypeLimit[],
    roomTypeId: string,
    date: string
  ): { minPrice: number; maxPrice: number } | null {
    return resolveEffectiveLimit(allLimits, roomTypeId, date);
  }

  async getRoomTypeLimit(
    propertyId: string,
    roomTypeId: string
  ): Promise<RoomTypeLimit | null> {
    try {
      const { data, error } = await supabase
        .from('room_type_limits')
        .select('*')
        .eq('property_id', propertyId)
        .eq('room_type_id', roomTypeId)
        .is('valid_from', null)
        .eq('scope', 'room_type')
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return await this.createDefaultLimit(propertyId, roomTypeId);
        }
        throw error;
      }

      return mapRowToLimit(data);
    } catch (error) {
      console.error('Error fetching room type limit:', error);
      return null;
    }
  }

  async getPropertyLimits(propertyId: string): Promise<RoomTypeLimit[]> {
    try {
      const { data, error } = await supabase
        .from('room_type_limits')
        .select('*')
        .eq('property_id', propertyId)
        .is('valid_from', null)
        .eq('scope', 'room_type');

      if (error) throw error;

      return (data || []).map(mapRowToLimit);
    } catch (error) {
      console.error('Error fetching property limits:', error);
      return [];
    }
  }

  async checkOverlap(
    propertyId: string,
    roomTypeId: string | null,
    scope: LimitScope,
    validFrom: string,
    validUntil: string,
    excludeId?: string
  ): Promise<{ overlaps: boolean; conflicts: RoomTypeLimit[] }> {
    try {
      let query = supabase
        .from('room_type_limits')
        .select('*')
        .eq('property_id', propertyId)
        .eq('scope', scope)
        .not('valid_from', 'is', null)
        .lte('valid_from', validUntil)
        .gte('valid_until', validFrom);

      if (scope === 'room_type' && roomTypeId) {
        query = query.eq('room_type_id', roomTypeId);
      } else if (scope === 'property') {
        query = query.is('room_type_id', null);
      }

      const { data, error } = await query;

      if (error) throw error;

      const conflicts = (data || [])
        .map(mapRowToLimit)
        .filter((l) => l.id !== excludeId);

      return { overlaps: conflicts.length > 0, conflicts };
    } catch (error) {
      console.error('Error checking overlap:', error);
      return { overlaps: false, conflicts: [] };
    }
  }

  async upsertLimit(
    propertyId: string,
    limit: PriceLimitTimeframe
  ): Promise<{ success: boolean; data?: RoomTypeLimit; error?: string }> {
    try {
      if (!isValidUuid(propertyId)) {
        return { success: false, error: 'Invalid property id' };
      }
      await assertPropertyAccess(propertyId);

      const row = {
        property_id: propertyId,
        room_type_id: limit.roomTypeId || null,
        min_price: limit.minPrice,
        max_price: limit.maxPrice,
        currency: limit.currency || 'EUR',
        valid_from: limit.validFrom || null,
        valid_until: limit.validUntil || null,
        scope: limit.scope,
        label: limit.label || null,
        updated_at: new Date().toISOString(),
      };

      let result;

      if (limit.id) {
        // Update existing by id
        const { data, error } = await supabase
          .from('room_type_limits')
          .update(row)
          .eq('id', limit.id)
          .select()
          .single();
        if (error) throw error;
        result = data;
      } else if (limit.validFrom === null) {
        // Default limit — find existing by property+roomType+scope, then update or insert
        let query = supabase
          .from('room_type_limits')
          .select('id')
          .eq('property_id', propertyId)
          .eq('scope', limit.scope)
          .is('valid_from', null);

        if (limit.roomTypeId) {
          query = query.eq('room_type_id', limit.roomTypeId);
        } else {
          query = query.is('room_type_id', null);
        }

        const { data: existing } = await query.maybeSingle();

        if (existing) {
          const { data, error } = await supabase
            .from('room_type_limits')
            .update(row)
            .eq('id', existing.id)
            .select()
            .single();
          if (error) throw error;
          result = data;
        } else {
          const { data, error } = await supabase
            .from('room_type_limits')
            .insert(row)
            .select()
            .single();
          if (error) throw error;
          result = data;
        }
      } else {
        // New time-framed limit — plain insert
        const { data, error } = await supabase
          .from('room_type_limits')
          .insert(row)
          .select()
          .single();
        if (error) throw error;
        result = data;
      }

      this.invalidatePropertyCache(propertyId);

      return { success: true, data: mapRowToLimit(result) };
    } catch (error) {
      if (error instanceof PropertyAuthorizationError) {
        return { success: false, error: 'Access denied' };
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async deleteLimit(
    limitId: string,
    propertyId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      if (!isValidUuid(propertyId) || !isValidUuid(limitId)) {
        return { success: false, error: 'Invalid id' };
      }
      // Authorize the propertyId argument; we additionally constrain the
      // delete by property_id below so a stale or attacker-supplied limitId
      // from another tenant cannot be deleted even if the caller is on a
      // different property they do own.
      await assertPropertyAccess(propertyId);

      const { error } = await supabase
        .from('room_type_limits')
        .delete()
        .eq('id', limitId)
        .eq('property_id', propertyId)
        .not('valid_from', 'is', null);

      if (error) throw error;

      this.invalidatePropertyCache(propertyId);
      return { success: true };
    } catch (error) {
      if (error instanceof PropertyAuthorizationError) {
        return { success: false, error: 'Access denied' };
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async validatePrice(
    propertyId: string,
    roomTypeId: string,
    price: number
  ): Promise<{
    isValid: boolean;
    violations: PriceLimitViolation[];
    adjustedPrice?: number;
    limit?: RoomTypeLimit;
  }> {
    const validationError = validateRequiredFields(
      { propertyId, roomTypeId, price },
      ['propertyId', 'roomTypeId', 'price']
    );

    if (validationError) {
      logError(validationError, {
        operation: 'validatePrice',
        propertyId,
        additionalData: { roomTypeId, price },
      });
      return {
        isValid: false,
        violations: [
          {
            type: 'min',
            currentPrice: price,
            limitPrice: 0,
            roomTypeId,
            propertyId,
            message: validationError.userMessage || 'Invalid input parameters',
          },
        ],
      };
    }

    if (typeof price !== 'number' || isNaN(price) || !isFinite(price)) {
      const error: DatabaseError = {
        code: ERROR_CODES.VALIDATION_ERROR,
        message: 'Price must be a valid number',
        severity: 'medium',
        retryable: false,
        userMessage: 'Please enter a valid price',
      };

      logError(error, {
        operation: 'validatePrice',
        propertyId,
        additionalData: { roomTypeId, price },
      });

      return {
        isValid: false,
        violations: [
          {
            type: 'min',
            currentPrice: price,
            limitPrice: 0,
            roomTypeId,
            propertyId,
            message: error.userMessage!,
          },
        ],
      };
    }

    const limit = await this.getRoomTypeLimit(propertyId, roomTypeId);

    if (!limit) {
      const basicValidation = {
        isValid: price > 0 && price < 10000,
        violations: [] as PriceLimitViolation[],
      };

      if (price <= 0) {
        basicValidation.violations.push({
          type: 'min' as const,
          currentPrice: price,
          limitPrice: 1,
          roomTypeId,
          propertyId,
          message: 'Price must be positive',
        });
      }

      if (price >= 10000) {
        basicValidation.violations.push({
          type: 'max' as const,
          currentPrice: price,
          limitPrice: 9999,
          roomTypeId,
          propertyId,
          message: 'Price exceeds reasonable maximum',
        });
      }

      return basicValidation;
    }

    const violations: PriceLimitViolation[] = [];
    let adjustedPrice = price;

    if (price < limit.minPrice) {
      violations.push({
        type: 'min',
        currentPrice: price,
        limitPrice: limit.minPrice,
        roomTypeId,
        propertyId,
        message: `Price $${price} is below minimum allowed price of $${limit.minPrice}`,
      });
      adjustedPrice = limit.minPrice;
    }

    if (price > limit.maxPrice) {
      violations.push({
        type: 'max',
        currentPrice: price,
        limitPrice: limit.maxPrice,
        roomTypeId,
        propertyId,
        message: `Price $${price} exceeds maximum allowed price of $${limit.maxPrice}`,
      });
      adjustedPrice = limit.maxPrice;
    }

    if (violations.length > 0) {
      const violationError: DatabaseError = {
        code: ERROR_CODES.BUSINESS_RULE_VIOLATION,
        message: `Price validation failed: ${violations.map((v) => v.message).join(', ')}`,
        severity: 'medium',
        retryable: false,
        userMessage: violations[0].message,
      };

      logError(violationError, {
        operation: 'validatePrice',
        propertyId,
        additionalData: { roomTypeId, price, violations, limit },
      });
    }

    return {
      isValid: violations.length === 0,
      violations,
      adjustedPrice: violations.length > 0 ? adjustedPrice : undefined,
      limit,
    };
  }

  private async createDefaultLimit(
    propertyId: string,
    roomTypeId: string
  ): Promise<RoomTypeLimit | null> {
    try {
      const defaultLimit = {
        property_id: propertyId,
        room_type_id: roomTypeId,
        min_price: 50,
        max_price: 500,
        currency: 'EUR',
        scope: 'room_type',
        valid_from: null,
        valid_until: null,
        label: null,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('room_type_limits')
        .insert(defaultLimit)
        .select()
        .single();

      if (error) {
        console.error('Error creating default limit:', error);
        return null;
      }

      this.invalidatePropertyCache(propertyId);
      return mapRowToLimit(data);
    } catch (error) {
      console.error('Error creating default room type limit:', error);
      return null;
    }
  }

  async updateLimit(params: {
    propertyId: string;
    roomTypeId: string;
    minPrice?: number;
    maxPrice?: number;
    currency?: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const updates: Record<string, string | number | null> = {
        updated_at: new Date().toISOString(),
      };

      if (params.minPrice !== undefined) updates.min_price = params.minPrice;
      if (params.maxPrice !== undefined) updates.max_price = params.maxPrice;
      if (params.currency !== undefined) updates.currency = params.currency;

      const { error } = await supabase
        .from('room_type_limits')
        .update(updates)
        .eq('property_id', params.propertyId)
        .eq('room_type_id', params.roomTypeId)
        .is('valid_from', null)
        .eq('scope', 'room_type');

      if (error) throw error;

      this.invalidatePropertyCache(params.propertyId);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  private isPropertyCacheValid(cacheKey: string): boolean {
    const expiry = this.propertyCacheExpiry.get(cacheKey);
    return (
      expiry !== undefined &&
      Date.now() < expiry &&
      this.propertyCache.has(cacheKey)
    );
  }

  private invalidatePropertyCache(propertyId: string): void {
    const cacheKey = `property-${propertyId}`;
    this.propertyCache.delete(cacheKey);
    this.propertyCacheExpiry.delete(cacheKey);
  }

  clearCache(propertyId?: string): void {
    if (propertyId) {
      this.invalidatePropertyCache(propertyId);
    } else {
      this.propertyCache.clear();
      this.propertyCacheExpiry.clear();
    }
  }

  async getPriceRecommendation(
    propertyId: string,
    roomTypeId: string,
    currentPrice: number
  ): Promise<{
    recommendation: 'optimal' | 'too_low' | 'too_high' | 'within_range';
    suggestedPrice?: number;
    message: string;
    limit?: RoomTypeLimit;
  }> {
    const limit = await this.getRoomTypeLimit(propertyId, roomTypeId);

    if (!limit) {
      return {
        recommendation: currentPrice > 0 ? 'within_range' : 'too_low',
        message: 'No price limits configured for this room type',
      };
    }

    const range = limit.maxPrice - limit.minPrice;
    const optimalMin = limit.minPrice + range * 0.2;
    const optimalMax = limit.maxPrice - range * 0.2;

    if (currentPrice < limit.minPrice) {
      return {
        recommendation: 'too_low',
        suggestedPrice: limit.minPrice,
        message: `Price is below minimum limit of $${limit.minPrice}`,
        limit,
      };
    }

    if (currentPrice > limit.maxPrice) {
      return {
        recommendation: 'too_high',
        suggestedPrice: limit.maxPrice,
        message: `Price exceeds maximum limit of $${limit.maxPrice}`,
        limit,
      };
    }

    if (currentPrice >= optimalMin && currentPrice <= optimalMax) {
      return {
        recommendation: 'optimal',
        message: 'Price is within optimal range',
        limit,
      };
    }

    return {
      recommendation: 'within_range',
      message: 'Price is within allowed limits',
      limit,
    };
  }
}

export function resolveEffectiveLimit(
  allLimits: RoomTypeLimit[],
  roomTypeId: string,
  date: string
): { minPrice: number; maxPrice: number } | null {
  let roomTypeTimeframed: RoomTypeLimit | undefined;
  let propertyTimeframed: RoomTypeLimit | undefined;
  let roomTypeDefault: RoomTypeLimit | undefined;
  let propertyDefault: RoomTypeLimit | undefined;

  for (const limit of allLimits) {
    const isDefault = limit.validFrom === null;
    const coversDate =
      !isDefault && limit.validFrom! <= date && limit.validUntil! >= date;

    if (limit.scope === 'room_type' && limit.roomTypeId === roomTypeId) {
      if (coversDate && !roomTypeTimeframed) {
        roomTypeTimeframed = limit;
      } else if (isDefault && !roomTypeDefault) {
        roomTypeDefault = limit;
      }
    } else if (limit.scope === 'property') {
      if (coversDate && !propertyTimeframed) {
        propertyTimeframed = limit;
      } else if (isDefault && !propertyDefault) {
        propertyDefault = limit;
      }
    }
  }

  const winner =
    roomTypeTimeframed ||
    propertyTimeframed ||
    roomTypeDefault ||
    propertyDefault;

  if (!winner) return null;

  return { minPrice: winner.minPrice, maxPrice: winner.maxPrice };
}

export const roomTypeLimitsService = new RoomTypeLimitsService();
